import { DEFAULT_STEP_IDS, STEP_DEFINITIONS } from './constants.js';
import { getDisclaimerText } from './disclaimer.js';
import { assertCompliantSnapshotRuleCopy } from './gochar-copy-lint.js';
import { renderAspectMatrix } from './charts/aspect-matrix.js';
import { renderGocharaKaksha } from './charts/kaksha.js';
import { renderKotaChakra } from './charts/kota.js';
import { renderNorthIndianGochar } from './charts/north-indian-chart.js';
import { renderPositionTables } from './charts/positions.js';
import { renderSarvatobhadra } from './charts/sarvatobhadra.js';
import { renderTransitWheel } from './charts/transit-wheel.js';
import {
  addWallClockStep,
  clearElement,
  dateTimeLocalToNaiveDate,
  downloadSvg,
  formatDateTimeForZone,
  htmlElement,
} from './utils.js';

const TABS = Object.freeze([
  { id: 'transit', label: 'Gochar Transit Wheel' },
  { id: 'rashi', label: 'North Indian Gochar Rashi Chart' },
  { id: 'sarvatobhadra', label: 'Sarvatobhadra Chakra' },
  { id: 'kota', label: 'Kota Chakra' },
  { id: 'kaksha', label: 'Gochara Kaksha' },
  { id: 'aspects', label: 'Western Aspect Analysis / Aspect Matrix' },
  { id: 'positions', label: 'Planetary Position Tables' },
]);

export class AdvancedAstrologyToolsModule {
  constructor(root, options = {}) {
    if (!(root instanceof Element)) throw new TypeError('root must be a DOM Element.');
    if (!options.client || typeof options.client.loadSnapshot !== 'function') {
      throw new TypeError('options.client.loadSnapshot(params) is required.');
    }

    this.root = root;
    this.options = {
      title: 'Professional Tools',
      futureYears: 10,
      restrictToFuture: true,
      expertMode: false,
      disclaimerLanguage: 'en',
      refreshDebounceMs: 250,
      animationIntervalMs: 850,
      realtimeIntervalMs: 3_000,
      charts: [{ id: options.chartId || 'default', name: options.chartName || 'Selected chart' }],
      initialTab: 'transit',
      ...options,
    };
    this.client = this.options.client;
    this.state = {
      tab: this.options.initialTab,
      snapshot: null,
      loading: false,
      error: null,
      mode: 'manual',
      animationStep: 'day',
      expertMode: Boolean(this.options.expertMode),
      requestSequence: 0,
    };
    this.abortController = null;
    this.refreshTimer = null;
    this.modeTimer = null;
    this.elements = {};
    this.renderShell();
    this.refresh();
  }

  destroy() {
    this.stopMode();
    this.abortController?.abort();
    clearTimeout(this.refreshTimer);
    clearElement(this.root);
  }

  // External autocomplete selects by chart ID without resetting target time or tabs.
  setChart(chartId) {
    const chart = this.options.charts.find(option => String(option.id) === String(chartId));
    if (!chart) return;
    this.elements.chartSelect.value = String(chart.id);
    if (Number.isFinite(chart.latitude)) this.elements.latitudeInput.value = chart.latitude;
    if (Number.isFinite(chart.longitude)) this.elements.longitudeInput.value = chart.longitude;
    this.scheduleRefresh();
  }

  renderShell() {
    clearElement(this.root);
    this.root.classList.add('aat-module');

    const header = htmlElement('header', { className: 'aat-module-header' });
    const heading = htmlElement('div', { className: 'aat-heading' }, [
      htmlElement('p', { className: 'aat-eyebrow', text: 'Kundli Star' }),
      htmlElement('h2', { text: this.options.title }),
      htmlElement('p', { className: 'aat-subtitle', text: 'Birth chart · Gochar · Sarvatobhadra · Kota · Kaksha' }),
    ]);
    const headerActions = htmlElement('div', { className: 'aat-header-actions' });
    const exportButton = htmlElement('button', { type: 'button', className: 'aat-button secondary', text: 'Export active SVG' });
    exportButton.addEventListener('click', () => this.exportActiveSvg());
    headerActions.append(exportButton);
    header.append(heading, headerActions);

    const controls = this.buildControls();
    const tabs = this.buildTabs();
    const status = htmlElement('div', { className: 'aat-status', role: 'status', 'aria-live': 'polite' });
    const content = htmlElement('section', { className: 'aat-content' });
    const metadata = htmlElement('section', { className: 'aat-metadata' });

    this.elements = { ...this.elements, header, controls, tabs, status, content, metadata, exportButton };
    this.root.append(header, controls, tabs, status, content, metadata);
  }

  buildControls() {
    const panel = htmlElement('section', { className: 'aat-control-panel' });
    const primary = htmlElement('div', { className: 'aat-primary-controls' });

    const chartSelect = htmlElement('select', { className: 'aat-input', name: 'chartId' });
    this.options.charts.forEach((chart) => {
      chartSelect.append(htmlElement('option', { value: chart.id, text: chart.name }));
    });
    chartSelect.value = this.options.chartId || this.options.charts[0]?.id || 'default';

    const timezone = this.options.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
    const localDateTime = this.options.localDateTime || formatDateTimeForZone(new Date(), timezone);
    const dateInput = htmlElement('input', {
      className: 'aat-input',
      type: 'datetime-local',
      step: this.state.expertMode ? '1' : '60',
      value: localDateTime,
      name: 'localDateTime',
    });
    const timezoneInput = htmlElement('input', {
      className: 'aat-input',
      type: 'text',
      value: timezone,
      name: 'timezone',
      list: 'aat-timezones',
      placeholder: 'Asia/Kolkata',
    });
    const timezoneList = htmlElement('datalist', { id: 'aat-timezones' });
    ['Asia/Kolkata', 'Europe/Stockholm', 'Europe/London', 'America/New_York', 'America/Chicago', 'Asia/Dubai', 'Asia/Singapore'].forEach((zone) => {
      timezoneList.append(htmlElement('option', { value: zone }));
    });
    const latitudeInput = htmlElement('input', {
      className: 'aat-input', type: 'number', step: '0.0001', min: '-90', max: '90',
      value: this.options.latitude ?? 22.5726, name: 'latitude',
    });
    const longitudeInput = htmlElement('input', {
      className: 'aat-input', type: 'number', step: '0.0001', min: '-180', max: '180',
      value: this.options.longitude ?? 88.3639, name: 'longitude',
    });
    const ayanamshaSelect = selectInput('ayanamsha', [
      ['lahiri', 'Lahiri'],
      ['raman', 'Raman'],
      ['krishnamurti', 'Krishnamurti'],
      ['tropical', 'Tropical'],
    ], this.options.ayanamsha || 'lahiri');
    const houseSystemSelect = selectInput('houseSystem', [
      ['placidus', 'Placidus'],
      ['whole-sign', 'Whole sign'],
      ['equal', 'Equal'],
    ], this.options.houseSystem || 'placidus');
    const nodeModeSelect = selectInput('nodeMode', [
      ['true', 'True nodes'],
      ['mean', 'Mean nodes'],
    ], this.options.nodeMode || 'true');

    primary.append(
      this.options.chartSelectorElement || field('Base chart', chartSelect),
      field('Target date & time', dateInput),
      field('Timezone', timezoneInput, timezoneList),
      field('Latitude', latitudeInput),
      field('Longitude', longitudeInput),
      field('Ayanamsha', ayanamshaSelect),
      field('House system', houseSystemSelect),
      field('Rahu/Ketu', nodeModeSelect),
    );

    const modeBar = htmlElement('div', { className: 'aat-mode-bar' });
    const todayButton = button('Now', 'secondary');
    const manualButton = button('Manual', 'is-active');
    const realtimeButton = button('Realtime');
    const animateButton = button('Animate');
    const stopButton = button('Stop', 'danger');
    const animationSelect = selectInput('animationStep', [], this.state.animationStep);
    animationSelect.classList.add('aat-animation-step');
    const expertCheckbox = htmlElement('input', {
      type: 'checkbox',
      name: 'expertMode',
      checked: this.state.expertMode,
      'aria-label': 'Expert mode',
    });
    const expertToggle = htmlElement('label', { className: 'aat-expert-toggle' }, [
      expertCheckbox,
      htmlElement('span', { text: 'Expert mode' }),
    ]);

    todayButton.addEventListener('click', () => {
      dateInput.value = formatDateTimeForZone(new Date(), timezoneInput.value || timezone);
      this.stopMode();
      this.refresh();
    });
    manualButton.addEventListener('click', () => this.setMode('manual'));
    realtimeButton.addEventListener('click', () => this.setMode('realtime'));
    animateButton.addEventListener('click', () => this.setMode('animate'));
    stopButton.addEventListener('click', () => this.stopMode());
    animationSelect.addEventListener('change', () => { this.state.animationStep = animationSelect.value; });
    expertCheckbox.addEventListener('change', () => this.setExpertMode(expertCheckbox.checked));

    modeBar.append(todayButton, manualButton, realtimeButton, animateButton, stopButton, animationSelect, expertToggle);

    const steppers = htmlElement('div', { className: 'aat-steppers' });

    const refreshOnChange = [chartSelect, dateInput, timezoneInput, latitudeInput, longitudeInput, ayanamshaSelect, houseSystemSelect, nodeModeSelect];
    refreshOnChange.forEach((element) => {
      const eventName = element.tagName === 'INPUT' && ['text', 'number'].includes(element.type) ? 'input' : 'change';
      element.addEventListener(eventName, () => this.scheduleRefresh());
    });

    panel.append(primary, modeBar, steppers);
    this.elements = {
      ...this.elements,
      chartSelect,
      dateInput,
      timezoneInput,
      latitudeInput,
      longitudeInput,
      ayanamshaSelect,
      houseSystemSelect,
      nodeModeSelect,
      animationSelect,
      expertCheckbox,
      steppers,
      manualButton,
      realtimeButton,
      animateButton,
      stopButton,
    };
    this.renderStepControls();
    return panel;
  }

  visibleStepDefinitions() {
    return this.state.expertMode
      ? STEP_DEFINITIONS
      : STEP_DEFINITIONS.filter((step) => DEFAULT_STEP_IDS.includes(step.id));
  }

  renderStepControls() {
    const visibleSteps = this.visibleStepDefinitions();
    clearElement(this.elements.steppers);
    visibleSteps.forEach((step) => {
      const row = htmlElement('div', { className: 'aat-step-row', dataset: { step: step.id } });
      const minus = button('−', 'icon');
      const label = htmlElement('span', { text: step.label });
      const plus = button('+', 'icon');
      minus.title = `Subtract ${step.label}`;
      plus.title = `Add ${step.label}`;
      minus.addEventListener('click', () => this.stepDate(step, -1));
      plus.addEventListener('click', () => this.stepDate(step, 1));
      row.append(minus, label, plus);
      this.elements.steppers.append(row);
    });

    clearElement(this.elements.animationSelect);
    visibleSteps.forEach((step) => {
      this.elements.animationSelect.append(htmlElement('option', { value: step.id, text: step.label }));
    });
    if (!visibleSteps.some((step) => step.id === this.state.animationStep)) {
      this.state.animationStep = 'day';
    }
    this.elements.animationSelect.value = this.state.animationStep;
    this.elements.dateInput.step = this.state.expertMode ? '1' : '60';
  }

  setExpertMode(enabled) {
    this.state.expertMode = Boolean(enabled);
    this.elements.expertCheckbox.checked = this.state.expertMode;
    this.renderStepControls();
  }

  buildTabs() {
    const nav = htmlElement('nav', { className: 'aat-tabs', 'aria-label': 'Advanced astrology charts' });
    TABS.forEach((tab) => {
      const tabButton = htmlElement('button', {
        type: 'button',
        className: `aat-tab${tab.id === this.state.tab ? ' is-active' : ''}`,
        text: tab.label,
        dataset: { tab: tab.id },
        role: 'tab',
        'aria-selected': tab.id === this.state.tab ? 'true' : 'false',
      });
      tabButton.addEventListener('click', () => this.setTab(tab.id));
      nav.append(tabButton);
    });
    return nav;
  }

  setTab(tabId) {
    if (!TABS.some((tab) => tab.id === tabId)) return;
    this.state.tab = tabId;
    this.elements.tabs.querySelectorAll('.aat-tab').forEach((buttonElement) => {
      const active = buttonElement.dataset.tab === tabId;
      buttonElement.classList.toggle('is-active', active);
      buttonElement.setAttribute('aria-selected', String(active));
    });
    this.renderActiveTab();
  }

  setMode(mode) {
    this.stopMode();
    this.state.mode = mode;
    this.updateModeButtons();

    if (mode === 'realtime') {
      const tick = () => {
        this.elements.dateInput.value = formatDateTimeForZone(new Date(), this.elements.timezoneInput.value || 'UTC');
        this.refresh();
      };
      tick();
      this.modeTimer = setInterval(tick, this.options.realtimeIntervalMs);
    } else if (mode === 'animate') {
      const tick = () => {
        const step = STEP_DEFINITIONS.find((item) => item.id === this.state.animationStep) || STEP_DEFINITIONS[0];
        this.stepDate(step, 1, { stopMode: false });
      };
      this.modeTimer = setInterval(tick, this.options.animationIntervalMs);
    }
  }

  stopMode() {
    if (this.modeTimer) clearInterval(this.modeTimer);
    this.modeTimer = null;
    this.state.mode = 'manual';
    this.updateModeButtons();
  }

  updateModeButtons() {
    const pairs = [
      ['manual', this.elements.manualButton],
      ['realtime', this.elements.realtimeButton],
      ['animate', this.elements.animateButton],
    ];
    pairs.forEach(([mode, element]) => element?.classList.toggle('is-active', this.state.mode === mode));
  }

  stepDate(step, direction, { stopMode = true } = {}) {
    if (stopMode) this.stopMode();
    try {
      const next = addWallClockStep(this.elements.dateInput.value, step.unit, step.amount * direction);
      this.assertDateRange(next);
      this.elements.dateInput.value = next;
      this.refresh();
    } catch (error) {
      this.showError(error);
      if (!stopMode) this.stopMode();
    }
  }

  assertDateRange(localDateTime) {
    const target = dateTimeLocalToNaiveDate(localDateTime);
    const zone = this.elements.timezoneInput.value || 'UTC';
    const nowValue = formatDateTimeForZone(new Date(), zone);
    const minimum = dateTimeLocalToNaiveDate(nowValue);
    const maximum = dateTimeLocalToNaiveDate(addWallClockStep(nowValue, 'years', this.options.futureYears));
    if (this.options.restrictToFuture && target < minimum) {
      throw new RangeError('The target cannot be earlier than the current date and time.');
    }
    if (target > maximum) {
      throw new RangeError(`The target cannot be more than ${this.options.futureYears} years ahead.`);
    }
  }

  scheduleRefresh() {
    clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => this.refresh(), this.options.refreshDebounceMs);
  }

  getParams() {
    const selectedChart = this.options.charts.find((chart) => String(chart.id) === String(this.elements.chartSelect.value));
    return {
      chartId: this.elements.chartSelect.value,
      chartName: selectedChart?.name,
      localDateTime: this.elements.dateInput.value,
      timezone: this.elements.timezoneInput.value,
      latitude: Number(this.elements.latitudeInput.value),
      longitude: Number(this.elements.longitudeInput.value),
      ayanamsha: this.elements.ayanamshaSelect.value,
      houseSystem: this.elements.houseSystemSelect.value,
      nodeMode: this.elements.nodeModeSelect.value,
    };
  }

  async refresh() {
    clearTimeout(this.refreshTimer);
    const params = this.getParams();
    try {
      this.assertDateRange(params.localDateTime);
    } catch (error) {
      this.showError(error);
      return;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();
    const sequence = ++this.state.requestSequence;
    this.state.loading = true;
    this.state.error = null;
    this.showStatus('Calculating Gochar charts…', 'loading');

    try {
      const snapshot = await this.client.loadSnapshot(params, { signal: this.abortController.signal });
      if (sequence !== this.state.requestSequence) return;
      assertCompliantSnapshotRuleCopy(snapshot);
      snapshot.meta = {
        ...(snapshot.meta || {}),
        disclaimerLanguage: this.options.disclaimerLanguage,
      };
      this.state.snapshot = snapshot;
      this.state.loading = false;
      this.showStatus('Calculation ready.', 'success');
      this.renderActiveTab();
      this.renderMetadata();
    } catch (error) {
      if (error?.name === 'AbortError') return;
      if (sequence !== this.state.requestSequence) return;
      this.state.loading = false;
      this.showError(error);
    }
  }

  renderActiveTab() {
    const { content } = this.elements;
    clearElement(content);
    if (!this.state.snapshot) {
      content.append(htmlElement('div', { className: 'aat-empty-state', text: 'Choose a chart and date to calculate.' }));
      return;
    }

    switch (this.state.tab) {
      case 'transit':
        renderTransitWheel(content, this.state.snapshot);
        break;
      case 'rashi':
        renderNorthIndianGochar(content, this.state.snapshot);
        break;
      case 'sarvatobhadra':
        renderSarvatobhadra(content, this.state.snapshot);
        break;
      case 'kota':
        renderKotaChakra(content, this.state.snapshot);
        break;
      case 'kaksha':
        renderGocharaKaksha(content, this.state.snapshot);
        break;
      case 'aspects':
        renderAspectMatrix(content, this.state.snapshot);
        break;
      case 'positions':
        renderPositionTables(content, this.state.snapshot);
        break;
      default:
        renderTransitWheel(content, this.state.snapshot);
    }
  }

  renderMetadata() {
    const meta = this.state.snapshot?.meta || {};
    const entries = [
      ['Date/time', meta.displayDateTime || meta.localDateTime || meta.atUtc],
      ['Timezone', meta.timezone],
      ['Location', Number.isFinite(meta.latitude) && Number.isFinite(meta.longitude) ? `${meta.latitude}, ${meta.longitude}` : null],
      ['Ayanamsha', meta.ayanamsha],
      ['House system', meta.houseSystem],
      ['Nodes', meta.nodeMode],
      ['Gochar rule set', meta.gocharRuleSetVersion],
      ['Kaksha data', meta.kakshaDataSource],
    ].filter(([, value]) => value !== null && value !== undefined && value !== '');
    clearElement(this.elements.metadata);
    this.elements.metadata.append(htmlElement('h3', { text: 'Calculation metadata' }));
    const grid = htmlElement('dl', { className: 'aat-metadata-grid' });
    entries.forEach(([label, value]) => {
      grid.append(htmlElement('div', {}, [
        htmlElement('dt', { text: label }),
        htmlElement('dd', { text: value }),
      ]));
    });
    this.elements.metadata.append(grid);
  }

  showStatus(message, type = 'info') {
    this.elements.status.className = `aat-status is-${type}`;
    this.elements.status.textContent = message;
  }

  showError(error) {
    const message = error instanceof Error ? error.message : String(error);
    this.state.error = error;
    this.showStatus(message, 'error');
  }

  exportActiveSvg() {
    const svg = this.elements.content.querySelector('svg');
    if (!svg) {
      this.showStatus('The active tab does not contain an SVG chart.', 'info');
      return;
    }
    const date = this.elements.dateInput.value.replaceAll(':', '-');
    downloadSvg(svg, `kundlistar-${this.state.tab}-${date}.svg`, {
      footerText: getDisclaimerText(this.options.disclaimerLanguage),
    });
  }
}

export function mountAdvancedAstrologyTools(root, options) {
  return new AdvancedAstrologyToolsModule(root, options);
}

function field(label, control, extra = null) {
  const wrapper = htmlElement('label', { className: 'aat-field' });
  wrapper.append(htmlElement('span', { className: 'aat-field-label', text: label }), control);
  if (extra) wrapper.append(extra);
  return wrapper;
}

function button(text, className = '') {
  return htmlElement('button', {
    type: 'button',
    className: `aat-button ${className}`.trim(),
    text,
  });
}

function selectInput(name, entries, selected) {
  const select = htmlElement('select', { className: 'aat-input', name });
  entries.forEach(([value, label]) => select.append(htmlElement('option', { value, text: label })));
  select.value = selected;
  return select;
}
